/**
 * PATH: packages/email-hub/src/feature/reader-header/tokens.ts
 * FILE_ID: EMAILHUB-READERHEADER-TOKENS-001
 * PURPOSE: One numeric source of truth for the Calm Command Header geometry contract.
 * ROLE IN ARCHITECTURE: Design-token layer shared by components, tests, and E2E budgets.
 * MAIN EXPORTS: READER_HEADER_TOKENS plus named band/motion/budget constants.
 * NON-RESPONSIBILITIES: Colors and typography stay on the shared HSL theme tokens.
 * NOTES FOR FUTURE AI: These numbers are product contracts (plan D269-09/13/14/17);
 *   change them only with an approved plan amendment, never to make a layout fit.
 */

/** Band 1 sticky command bar height (px). */
export const COMMAND_BAR_H = 44
/** Body scroll offset (px) after which the sticky bar paints its separation shadow. */
export const SCROLL_SHADOW_AFTER_PX = 8
/** Band 2 identity avatar size (px) — 32, not the legacy 40. */
export const AVATAR = 32
/** Resolved triage summary line height (px). */
export const SUMMARY_H = 32
/** Pending decision line height (px). */
export const DECISION_H = 40
/** Compact summary strip under a pending decision (px). */
export const SUMMARY_STRIP_H = 28
/** Hairline handle that closes the open context lanes (px). */
export const COLLAPSE_HANDLE_H = 12
/**
 * Context lane grid at the 660px acceptance width (px): two rows of the
 * one-row lane cells at the standard density tier, with headroom for one wrap.
 * This is the operator-approved cost of having the command centre on screen at
 * rest (2026-07-25: "it should not take half the screen but 100px is ok").
 *
 * A nominal row is py-1 (8) + the 24px ellipsis slot that floors it = ~32px,
 * so two rows land near 66px; the rest of this number is wrap allowance for a
 * dense placement/tag run. If a real render exceeds it, shrink the lane —
 * raising this is a body-budget grab and needs a plan amendment.
 */
export const CONTEXT_LANES_MAX = 104

/** Interactive target sizes (px) by pointer class. */
export const TARGET = {
  fine: 28,
  coarse: 44,
} as const

/**
 * Coarse-pointer floor for the decision band (change #269 C1). DECISION_H
 * (40px) is a FINE-pointer contract only — it has no room for a genuine
 * TARGET.coarse (44px) confirm/reject circle. On coarse pointers ONLY the
 * band grows to this floor via a `pointer-coarse:min-h-*` class (CSS
 * min-height clamps the used height above an inline `height:40px` style
 * regardless of cascade origin — see DecisionLine.tsx). Fine-pointer
 * geometry (DECISION_H itself, and every 660x900 budget assertion built on
 * it) never changes.
 */
export const DECISION_H_COARSE_MIN = TARGET.coarse

/** Motion budget (ms). Reduced-motion swaps to opacity-only transitions. */
export const MOTION_MS = {
  hover: 120,
  press: 80,
  expand: 160,
  collapse: 120,
  sticky: 150,
  toastIn: 200,
  toastOut: 150,
} as const

/**
 * Body-first vertical budget at the 660x900 acceptance viewport (plan
 * D269-09): first body glyph within 160px resolved / 190px pending of the
 * reader top, body at least 60% of the reader viewport on first paint.
 *
 * Change #294 adds the OPEN pair. D269-09 only ever described the collapsed
 * header, because the lanes were opt-in; they are now the resting state, so
 * the state the operator actually sees needs a budget of its own. The
 * collapsed numbers are unchanged and still enforced — closing the lanes must
 * return the header to exactly what shipped.
 *
 * openResolvedMax = command 44 + identity 56 + lanes 104 + handle 12.
 * openPendingMax  = the same plus the 40px decision line.
 * minBodyPct holds at 60 in every state: 900 - 256 = 644 = 71%.
 */
export const BODY_FIRST_BUDGET = {
  resolvedMax: 160,
  pendingMax: 190,
  openResolvedMax: 216,
  openPendingMax: 256,
  minBodyPct: 60,
} as const

/**
 * Action feedback timing contract (plan "Interaction speed and feedback
 * contract" + "Optimism, undo, failure, and concurrency"). The runner in
 * runtime/useReaderActionRunner.ts is the ONLY consumer allowed to schedule
 * from these; components read phases, never raw timers.
 */
export const ACTION_TIMING_MS = {
  /** Visible acknowledgement must land within this budget (same frame ideal). */
  ackMax: 100,
  /** No spinner before this — sub-400ms responses must feel instant. */
  spinnerDelay: 400,
  /** Switch to the "Still working…" state. */
  stillWorkingAt: 3_000,
  /** Abort the request and offer Retry — no permanent spinner, ever. */
  timeoutAt: 10_000,
  /** Undo toast window for reversible/terminal actions. */
  undoWindow: 8_000,
} as const

/**
 * Density tier floors (px container width) with hysteresis to prevent
 * breakpoint thrash while splitters/scrollbars move. Runtime behavior lives
 * in responsive/density.ts; these constants keep tests and components on the
 * same numbers.
 */
export const DENSITY_TIERS = {
  compactMin: 0,
  standardMin: 480,
  wideMin: 840,
  expandedMin: 1200,
  hysteresis: 16,
} as const

/** Aggregate export so tests can snapshot the entire numeric contract at once. */
export const READER_HEADER_TOKENS = {
  COMMAND_BAR_H,
  SCROLL_SHADOW_AFTER_PX,
  AVATAR,
  SUMMARY_H,
  DECISION_H,
  DECISION_H_COARSE_MIN,
  SUMMARY_STRIP_H,
  COLLAPSE_HANDLE_H,
  CONTEXT_LANES_MAX,
  TARGET,
  MOTION_MS,
  ACTION_TIMING_MS,
  BODY_FIRST_BUDGET,
  DENSITY_TIERS,
} as const
